import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  authClient,
  clearAccessToken,
  hasValidStoredAccessToken,
} from "@/integrations/auth/client";
import { stopTokenManager } from "@/integrations/auth/token-manager";
import { toast } from "sonner";
import { isNetworkError } from "@/lib/error-utils";
import { apiJson } from "@/lib/api-client";

type UserRole = "admin" | "employee" | "freelancer" | null;

interface AuthUser {
  id: string;
  email?: string;
  name?: string;
  image?: string;
}

interface AuthSession {
  user: AuthUser;
}

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  role: UserRole;
  personId: string | null;
  isOfflineAuth: boolean;
  isLoading: boolean;
  isEmployee: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_PROFILE_STORAGE_KEY = "production-hub.auth-profile";

type CachedAuthProfile = {
  user: AuthUser;
  role: UserRole;
  personId: string | null;
  savedAt: number;
};

type AuthProfileResponse = {
  person: {
    id: string;
    person_type: string | null;
    is_admin: boolean | null;
  } | null;
};

function readCachedAuthProfile(): CachedAuthProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(AUTH_PROFILE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedAuthProfile;
    if (!parsed.user?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedAuthProfile(profile: CachedAuthProfile) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    AUTH_PROFILE_STORAGE_KEY,
    JSON.stringify(profile),
  );
}

function clearCachedAuthProfile() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY);
}

function readValidCachedAuthProfile() {
  if (!hasValidStoredAccessToken()) return null;
  return readCachedAuthProfile();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    data: session,
    isPending,
    error: sessionError,
  } = authClient.useSession();

  const [initialCachedProfile] = useState<CachedAuthProfile | null>(() =>
    readValidCachedAuthProfile(),
  );
  const [offlineUser, setOfflineUser] = useState<AuthUser | null>(
    () => initialCachedProfile?.user ?? null,
  );
  const [role, setRole] = useState<UserRole>(
    () => initialCachedProfile?.role ?? null,
  );
  const [personId, setPersonId] = useState<string | null>(
    () => initialCachedProfile?.personId ?? null,
  );
  const [isOfflineAuth, setIsOfflineAuth] = useState(
    () => initialCachedProfile !== null,
  );
  const [isLoading, setIsLoading] = useState(
    () => initialCachedProfile === null,
  );

  const applyCachedProfileForNetworkError = useCallback((userId: string) => {
    const cached = readCachedAuthProfile();
    if (cached?.user.id === userId && hasValidStoredAccessToken()) {
      setOfflineUser(cached.user);
      setRole(cached.role);
      setPersonId(cached.personId);
      setIsOfflineAuth(true);
      return;
    }

    toast.error(
      "Unable to connect to the database. Please check your connection.",
    );
    setRole(null);
    setPersonId(null);
    setIsOfflineAuth(false);
  }, []);

  const fetchUserRole = useCallback(
    async (authUser: AuthUser) => {
      try {
        const { person } = await apiJson<AuthProfileResponse>(
          "/api/auth-profile/me",
        );

        if (person) {
          const resolvedRole = person.is_admin
            ? "admin"
            : person.person_type === "employee" ||
                person.person_type === "freelancer"
              ? person.person_type
              : null;
          setPersonId(person.id);
          setRole(resolvedRole);
          setIsOfflineAuth(false);
          writeCachedAuthProfile({
            user: authUser,
            role: resolvedRole,
            personId: person.id,
            savedAt: Date.now(),
          });
        } else {
          setRole(null);
          setPersonId(null);
          setIsOfflineAuth(false);
          clearCachedAuthProfile();
        }
      } catch (err) {
        console.error("Error fetching role:", err);
        if (isNetworkError(err)) {
          applyCachedProfileForNetworkError(authUser.id);
        } else {
          const cached = readCachedAuthProfile();
          if (cached?.user.id === authUser.id && hasValidStoredAccessToken()) {
            setOfflineUser(cached.user);
            setRole(cached.role);
            setPersonId(cached.personId);
            setIsOfflineAuth(true);
          } else {
            toast.error("Could not load your user profile.");
            setRole(null);
            setPersonId(null);
            setIsOfflineAuth(false);
          }
        }
      } finally {
        setIsLoading(false);
      }
    },
    [applyCachedProfileForNetworkError],
  );

  useEffect(() => {
    if (isPending) return;
    if (session?.user) {
      const cached = readCachedAuthProfile();
      const canUseCachedProfile =
        cached?.user.id === session.user.id && hasValidStoredAccessToken();

      if (canUseCachedProfile) {
        setOfflineUser(cached.user);
        setRole(cached.role);
        setPersonId(cached.personId);
        setIsOfflineAuth(true);
        setIsLoading(false);
      } else {
        setOfflineUser(null);
        setIsOfflineAuth(false);
        setIsLoading(true);
      }

      fetchUserRole(session.user);
    } else {
      const cached = readCachedAuthProfile();
      const canUseCachedProfile =
        cached &&
        hasValidStoredAccessToken() &&
        (!navigator.onLine || isNetworkError(sessionError));

      if (canUseCachedProfile) {
        setOfflineUser(cached.user);
        setRole(cached.role);
        setPersonId(cached.personId);
        setIsOfflineAuth(true);
      } else {
        setOfflineUser(null);
        setRole(null);
        setPersonId(null);
        setIsOfflineAuth(false);
      }
      setIsLoading(false);
    }
  }, [session, isPending, sessionError, fetchUserRole]);

  const signOut = async () => {
    stopTokenManager();
    clearAccessToken();
    clearCachedAuthProfile();
    await authClient.signOut();
    setOfflineUser(null);
    setRole(null);
    setPersonId(null);
    setIsOfflineAuth(false);
  };

  const isEmployee = role === "admin" || role === "employee";
  const user = session?.user ?? offlineUser;
  const effectiveSession =
    session ?? (offlineUser ? { user: offlineUser } : null);
  const hasCachedUser = offlineUser !== null;

  return (
    <AuthContext.Provider
      value={{
        user,
        session: effectiveSession,
        role,
        personId,
        isOfflineAuth,
        isLoading: (isPending && !hasCachedUser) || isLoading,
        isEmployee,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
